import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Flame, TrendingUp, Gem, Heart, X, Share2, BookOpen, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBooks } from "@/hooks/useBooks";
import { useUserBooks } from "@/hooks/useUserBooks";
import type { Book } from "@/data/books";
import {
  emotionalTagsFor,
  filterFeedBooks,
  matchScore,
  rankByMatch,
  rankByMood,
  readerPersonality,
  topAffinity,
  type ReaderSignals,
} from "@/lib/recommendations";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import MoodSelector from "@/components/MoodSelector";
import BookDna from "@/components/BookDna";
import ReactionBar from "@/components/ReactionBar";
import { buildBookDna } from "@/lib/bookDna";
import { loadMood, saveMood, moodById } from "@/lib/moods";
import { useMyReactionTallies, useReactionCountsForBooks } from "@/hooks/useReactions";
import DailyMatchCard from "@/components/DailyMatchCard";

const SectionHeader = ({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  subtitle?: string;
}) => (
  <div className="flex items-end justify-between mb-4 px-1">
    <div>
      <h2 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Icon className="w-6 h-6 text-primary" />
        {title}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  </div>
);

const FeedCard = ({
  book,
  score,
  onDismiss,
}: {
  book: Book;
  score: number;
  onDismiss: (b: Book) => void;
}) => {
  const { user } = useAuth();
  const { statusFor, setStatus } = useUserBooks();
  const saved = statusFor(book.id) === "tbr";
  const tags = useMemo(() => emotionalTagsFor(book), [book]);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to save");
    setStatus(book.id, "tbr");
    trackEvent("book_tbr", { book_id: book.id, source: "for_you_feed" });
  };
  const handleRead = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to mark as read");
    setStatus(book.id, "read");
  };
  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    const url = `${window.location.origin}/book/${book.id}`;
    try {
      if (navigator.share) await navigator.share({ title: book.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* ignore */ }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      className="group relative rounded-2xl overflow-hidden bg-card border border-border shadow-sm hover:shadow-xl transition-shadow"
    >
      <Link to={`/book/${book.id}`} className="block">
        <div
          className={`aspect-[3/4] bg-gradient-to-br ${book.coverColor} relative overflow-hidden`}
        >
          {book.coverImageUrl && (
            <img
              src={book.coverImageUrl}
              alt={`${book.title} cover`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
            />
          )}
          <div className="absolute top-3 left-3 bg-primary text-primary-foreground rounded-full px-2.5 py-1 text-xs font-bold shadow-lg">
            {score}% Match
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onDismiss(book);
            }}
            aria-label="Not interested"
            className="absolute top-3 right-3 rounded-full p-1.5 bg-background/70 backdrop-blur hover:bg-background text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
            <h3 className="font-display text-base font-bold text-white line-clamp-2">{book.title}</h3>
            <p className="text-xs text-white/80 line-clamp-1">{book.author}</p>
          </div>
        </div>
      </Link>
      <div className="p-3 space-y-2">
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wide bg-primary/15 text-primary px-2 py-0.5 rounded-full font-semibold">
                {t}
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground line-clamp-2">{book.hook}</p>
        <ReactionBar bookId={book.id} variant="compact" />
        <div className="flex items-center gap-1.5 pt-1">
          <Button size="sm" variant={saved ? "default" : "outline"} className="flex-1" onClick={handleSave}>
            <Heart className={`w-3.5 h-3.5 ${saved ? "fill-current" : ""}`} />
            {saved ? "Saved" : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleRead} aria-label="Mark as read">
            <BookOpen className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleShare} aria-label="Share">
            <Share2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const Section = ({
  icon,
  title,
  subtitle,
  books,
  signals,
  onDismiss,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  subtitle?: string;
  books: Book[];
  signals: ReaderSignals;
  onDismiss: (b: Book) => void;
}) => {
  if (books.length === 0) return null;
  return (
    <section className="mb-12">
      <SectionHeader icon={icon} title={title} subtitle={subtitle} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {books.map((b) => (
          <FeedCard key={b.id} book={b} score={matchScore(b, signals)} onDismiss={onDismiss} />
        ))}
      </div>
    </section>
  );
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export const ForYouFeed = () => {
  const { user, profile } = useAuth();
  const { books } = useBooks();
  const { items: userBooks, refresh: refreshUserBooks } = useUserBooks();
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [favoriteTropes, setFavoriteTropes] = useState<string[]>([]);
  const [favoriteSubgenres, setFavoriteSubgenres] = useState<string[]>([]);
  const [emotionalPref, setEmotionalPref] = useState<string | null>(null);
  const [spicePref, setSpicePref] = useState<number | null>(null);
  const [visibleNew, setVisibleNew] = useState(8);
  const [moodId, setMoodId] = useState<string | null>(() => loadMood());
  const reactionTallies = useMyReactionTallies();

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const [{ data: prof }, { data: dis }] = await Promise.all([
        supabase
          .from("profiles")
          .select("favorite_tropes, favorite_subgenres, emotional_preference, spice_preference")
          .eq("id", user.id)
          .maybeSingle(),
        supabase.from("book_dismissals").select("book_id").eq("user_id", user.id),
      ]);
      if (!active) return;
      setFavoriteTropes(prof?.favorite_tropes ?? []);
      setFavoriteSubgenres(prof?.favorite_subgenres ?? []);
      setEmotionalPref(prof?.emotional_preference ?? null);
      setSpicePref(prof?.spice_preference ?? null);
      setDismissedIds((dis ?? []).map((d) => d.book_id));
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const savedIds = useMemo(() => userBooks.filter((u) => u.status === "tbr").map((u) => u.book_id), [userBooks]);
  const readIds = useMemo(() => userBooks.filter((u) => u.status === "read").map((u) => u.book_id), [userBooks]);

  const signals: ReaderSignals = useMemo(
    () => ({
      favoriteTropes,
      favoriteSubgenres,
      emotionalPreference: emotionalPref,
      spicePreference: spicePref,
      savedBookIds: savedIds,
      readBookIds: readIds,
      dismissedBookIds: dismissedIds,
    }),
    [favoriteTropes, favoriteSubgenres, emotionalPref, spicePref, savedIds, readIds, dismissedIds],
  );

  const handleDismiss = useCallback(
    async (b: Book) => {
      if (!user) return toast.error("Sign in to personalize");
      setDismissedIds((prev) => [...prev, b.id]);
      const { error } = await supabase
        .from("book_dismissals")
        .insert({ user_id: user.id, book_id: b.id });
      if (error && !`${error.message}`.includes("duplicate")) {
        toast.error("Could not save preference");
        setDismissedIds((prev) => prev.filter((x) => x !== b.id));
        return;
      }
      trackEvent("book_skipped", { book_id: b.id, source: "for_you_feed" });
      toast.success("We'll show you fewer like this");
    },
    [user],
  );

  const pool = useMemo(() => filterFeedBooks(books, signals), [books, signals]);
  const baseRanked = useMemo(() => rankByMatch(pool, signals), [pool, signals]);
  const ranked = useMemo(() => rankByMood(baseRanked, moodId), [baseRanked, moodId]);
  const activeMood = moodById(moodId);

  const handleMoodChange = useCallback((id: string | null) => {
    setMoodId(id);
    saveMood(id);
    if (id) trackEvent("book_tbr", { mood: id, kind: "mood_select" });
  }, []);

  // Build Book DNA from saved + read books and the user's own reactions
  const dnaSlices = useMemo(() => {
    const ids = new Set([...savedIds, ...readIds]);
    const affinityBooks = books.filter((b) => ids.has(b.id));
    return buildBookDna(affinityBooks, reactionTallies);
  }, [books, savedIds, readIds, reactionTallies]);

  // Trending reactions: most-reacted books in the last 14 days
  const [trendingReactionBooks, setTrendingReactionBooks] = useState<Book[]>([]);
  useEffect(() => {
    let active = true;
    (async () => {
      const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
      const { data } = await supabase
        .from("book_reactions" as never)
        .select("book_id")
        .gte("created_at", since)
        .limit(500);
      if (!active) return;
      const counts: Record<string, number> = {};
      for (const r of (data ?? []) as Array<{ book_id: string }>) {
        counts[r.book_id] = (counts[r.book_id] ?? 0) + 1;
      }
      const topIds = Object.entries(counts)
        .sort((a, z) => z[1] - a[1])
        .slice(0, 8)
        .map(([id]) => id);
      const idSet = new Set(topIds);
      const list = books.filter((b) => idSet.has(b.id) && !signals.dismissedBookIds.includes(b.id));
      setTrendingReactionBooks(list);
    })();
    return () => { active = false; };
  }, [books, signals.dismissedBookIds]);

  const aff = topAffinity(signals);
  const becauseYouLove = useMemo(
    () =>
      aff.trope
        ? ranked
            .filter((b) => b.tropes.some((t) => t.toLowerCase() === aff.trope!.toLowerCase()))
            .slice(0, 8)
        : [],
    [ranked, aff.trope],
  );

  const trendingSubgenre = useMemo(
    () =>
      aff.subgenre
        ? ranked
            .filter((b) => (b.subgenre ?? []).some((s) => s.toLowerCase() === aff.subgenre!.toLowerCase()))
            .slice(0, 8)
        : [],
    [ranked, aff.subgenre],
  );

  const devastating = useMemo(
    () => ranked.filter((b) => b.emotionalIntensity === "devastating").slice(0, 8),
    [ranked],
  );

  const newReleases = useMemo(
    () =>
      [...pool]
        .filter((b) => b.createdAt)
        .sort((a, z) => (z.createdAt ?? "").localeCompare(a.createdAt ?? ""))
        .slice(0, visibleNew),
    [pool, visibleNew],
  );

  const hiddenGems = useMemo(
    () => ranked.filter((b) => !b.featured).slice(0, 8),
    [ranked],
  );

  const readersLikeYou = useMemo(() => ranked.slice(0, 8), [ranked]);

  const personality = useMemo(() => readerPersonality(signals), [signals]);
  const vibe = useMemo(() => {
    if (emotionalPref === "devastating") return "Emotionally devastating slow burns";
    if (emotionalPref === "light") return "Cozy, kick-your-feet romance";
    if (aff.trope) return `${aff.trope}-coded reads`;
    return "Tonight's romance picks";
  }, [emotionalPref, aff.trope]);

  const name = profile?.display_name || profile?.email?.split("@")[0] || "reader";

  return (
    <div className="container mx-auto px-4 pt-28 pb-20">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <p className="text-primary text-xs font-semibold tracking-widest uppercase mb-2">For You</p>
        <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-3">
          {greeting()}, <span className="text-gradient">{name}</span> 💖
        </h1>
        <p className="text-muted-foreground">
          Your current reading vibe: <span className="text-foreground font-medium">{vibe}</span>
        </p>
        <p className="text-xs text-muted-foreground/80 mt-1">
          Reader identity: <span className="text-primary">{personality}</span>
        </p>
      </motion.div>

      <DailyMatchCard />

      <MoodSelector value={moodId} onChange={handleMoodChange} />
      <BookDna identity={personality} slices={dnaSlices} />

      {activeMood && (
        <Section
          icon={Zap}
          title={<>Tuned to <span className="text-gradient">{activeMood.label}</span></>}
          subtitle="Re-ranked for your mood tonight"
          books={ranked.slice(0, 8)}
          signals={signals}
          onDismiss={handleDismiss}
        />
      )}

      {trendingReactionBooks.length > 0 && (
        <Section
          icon={Flame}
          title="Readers are screaming over these"
          subtitle="Most-reacted books in the last two weeks"
          books={trendingReactionBooks}
          signals={signals}
          onDismiss={handleDismiss}
        />
      )}

      {aff.trope && (
        <Section
          icon={Heart}
          title={<>Because you love <span className="text-gradient">{aff.trope}</span></>}
          subtitle="Hand-picked from your favorite tropes"
          books={becauseYouLove}
          signals={signals}
          onDismiss={handleDismiss}
        />
      )}

      <Section
        icon={Sparkles}
        title="Readers like you are obsessed with"
        subtitle="Based on your saves and reading history"
        books={readersLikeYou}
        signals={signals}
        onDismiss={handleDismiss}
      />

      <Section
        icon={Flame}
        title="Emotionally devastating reads"
        subtitle="For when you want to feel everything"
        books={devastating}
        signals={signals}
        onDismiss={handleDismiss}
      />

      {aff.subgenre && (
        <Section
          icon={TrendingUp}
          title={<>Trending in <span className="text-gradient">{aff.subgenre}</span></>}
          books={trendingSubgenre}
          signals={signals}
          onDismiss={handleDismiss}
        />
      )}

      <Section
        icon={Gem}
        title="Hidden indie gems"
        subtitle="Underexposed books we think you'll obsess over"
        books={hiddenGems}
        signals={signals}
        onDismiss={handleDismiss}
      />

      <Section
        icon={Sparkles}
        title="New releases you might love"
        books={newReleases}
        signals={signals}
        onDismiss={handleDismiss}
      />

      {newReleases.length >= visibleNew && pool.length > visibleNew && (
        <div className="text-center">
          <Button
            size="lg"
            variant="outline"
            onClick={() => {
              setVisibleNew((v) => v + 8);
              refreshUserBooks();
            }}
          >
            Load more matches
          </Button>
        </div>
      )}
    </div>
  );
};

export default ForYouFeed;
